import React, { useState, useRef } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, Alert, Badge, Button, Input } from 'reactstrap';
import BarcodeScannerModal from '../../Components/Common/BarcodeScannerModal';
import './BarcodeScannerDemo.css';

/**
 * Página de demostración del componente BarcodeScannerModal
 * Muestra diferentes casos de uso del escáner de códigos
 */
const BarcodeScannerDemo = () => {
    // Estados para los diferentes escáneres
    const [productScannerOpen, setProductScannerOpen] = useState(false);
    const [qrScannerOpen, setQrScannerOpen] = useState(false);
    const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
    const [multiScannerOpen, setMultiScannerOpen] = useState(false);

    // Estados para los resultados
    const [productCode, setProductCode] = useState('');
    const [qrData, setQrData] = useState('');
    const [barcode, setBarcode] = useState('');
    const [scannedItems, setScannedItems] = useState([]);

    // Referencia para prevenir duplicados
    const lastScanTimeRef = useRef(0);

    /**
     * Maneja el escaneo de producto (caso único)
     */
    const handleProductScan = (decodedText) => {
        setProductCode(decodedText);
        // Aquí se podría hacer una llamada a la API para obtener info del producto
        console.log('Producto escaneado:', decodedText);
    };

    /**
     * Maneja el escaneo de QR (datos estructurados)
     */
    const handleQrScan = (decodedText) => {
        try {
            // Intentar parsear como JSON
            const jsonData = JSON.parse(decodedText);
            setQrData(JSON.stringify(jsonData, null, 2));
        } catch {
            // Si no es JSON, mostrar como texto
            setQrData(decodedText);
        }
    };

    /**
     * Maneja el escaneo de código de barras
     */
    const handleBarcodeScan = (decodedText) => {
        setBarcode(decodedText);
    };

    /**
     * Maneja el escaneo múltiple (inventario)
     */
    const handleMultiScan = (decodedText) => {
        const now = Date.now();

        // Prevenir escaneos duplicados (debounce de 2 segundos)
        if (now - lastScanTimeRef.current < 2000) {
            return;
        }
        lastScanTimeRef.current = now;

        // Verificar si ya existe
        if (scannedItems.some(item => item.code === decodedText)) {
            alert('Este código ya fue escaneado');
            return;
        }

        const newItem = {
            id: Date.now(),
            code: decodedText,
            timestamp: new Date().toLocaleTimeString(),
        };

        setScannedItems(prev => [...prev, newItem]);
    };

    /**
     * Limpia la lista de items escaneados
     */
    const clearScannedItems = () => {
        setScannedItems([]);
        lastScanTimeRef.current = 0;
    };

    return (
        <div className="page-content">
            <Container fluid>
                {/* Header */}
                <Row className="mb-4">
                    <Col lg={12}>
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h4 className="fw-bold mb-1">
                                    <i className="bx bx-barcode-reader text-primary me-2"></i>
                                    Demostración Escáner de Códigos
                                </h4>
                                <p className="text-muted mb-0">
                                    Ejemplos de uso del componente BarcodeScannerModal con diferentes configuraciones
                                </p>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Ejemplo 1: Escaneo de Producto */}
                <Row className="mb-4">
                    <Col lg={6}>
                        <Card className="h-100">
                            <CardHeader className="bg-soft-primary">
                                <h5 className="card-title mb-0">
                                    <i className="bx bx-package me-2"></i>
                                    Ejemplo 1: Escaneo de Producto
                                </h5>
                            </CardHeader>
                            <CardBody>
                                <p className="text-muted mb-3">
                                    Escanea cualquier código (QR o de barras) para buscar un producto.
                                    El modal se cierra automáticamente después del escaneo.
                                </p>

                                <div className="mb-3">
                                    <label className="form-label">Código del Producto</label>
                                    <div className="input-group">
                                        <Input
                                            type="text"
                                            value={productCode}
                                            onChange={(e) => setProductCode(e.target.value)}
                                            placeholder="Presiona el botón para escanear..."
                                            readOnly
                                        />
                                        <Button
                                            color="primary"
                                            onClick={() => setProductScannerOpen(true)}
                                        >
                                            <i className="bx bx-scan me-1"></i>
                                            Escanear
                                        </Button>
                                    </div>
                                </div>

                                {productCode && (
                                    <Alert color="success" className="mb-0">
                                        <div className="d-flex align-items-center">
                                            <i className="bx bx-check-circle fs-4 me-2"></i>
                                            <div>
                                                <strong>¡Código detectado!</strong>
                                                <p className="mb-0">{productCode}</p>
                                            </div>
                                        </div>
                                    </Alert>
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Ejemplo 2: Escaneo de QR */}
                    <Col lg={6}>
                        <Card className="h-100">
                            <CardHeader className="bg-soft-info">
                                <h5 className="card-title mb-0">
                                    <i className="bx bx-qr me-2"></i>
                                    Ejemplo 2: Escaneo de QR
                                </h5>
                            </CardHeader>
                            <CardBody>
                                <p className="text-muted mb-3">
                                    Solo escanea códigos QR. Útil para autenticación o enlaces.
                                    Usa la cámara frontal por defecto.
                                </p>

                                <div className="mb-3">
                                    <Button
                                        color="info"
                                        className="w-100"
                                        onClick={() => setQrScannerOpen(true)}
                                    >
                                        <i className="bx bx-qr-scan me-1"></i>
                                        Escanear QR
                                    </Button>
                                </div>

                                {qrData && (
                                    <Alert color="info" className="mb-0">
                                        <div className="d-flex align-items-start">
                                            <i className="bx bx-qr-scan fs-4 me-2 mt-1"></i>
                                            <div className="flex-grow-1">
                                                <strong>Datos del QR:</strong>
                                                <pre className="mb-0 mt-2 bg-light p-2 rounded">
                                                    <code>{qrData}</code>
                                                </pre>
                                            </div>
                                        </div>
                                    </Alert>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Ejemplo 3: Escaneo de Código de Barras */}
                <Row className="mb-4">
                    <Col lg={6}>
                        <Card className="h-100">
                            <CardHeader className="bg-soft-success">
                                <h5 className="card-title mb-0">
                                    <i className="bx bx-barcode me-2"></i>
                                    Ejemplo 3: Escaneo de Código de Barras
                                </h5>
                            </CardHeader>
                            <CardBody>
                                <p className="text-muted mb-3">
                                    Solo escanea códigos de barras tradicionales (CODE-128, EAN-13, etc.).
                                    Ideal para inventario y punto de venta.
                                </p>

                                <div className="mb-3">
                                    <label className="form-label">Código Escaneado</label>
                                    <div className="scanner-result-box">
                                        {barcode ? (
                                            <div className="d-flex align-items-center">
                                                <Badge color="success" className="fs-6 p-2">
                                                    {barcode}
                                                </Badge>
                                                <Button
                                                    color="link"
                                                    className="text-danger ms-2"
                                                    onClick={() => setBarcode('')}
                                                >
                                                    <i className="bx bx-trash"></i>
                                                </Button>
                                            </div>
                                        ) : (
                                            <span className="text-muted">Sin código escaneado</span>
                                        )}
                                    </div>
                                </div>

                                <Button
                                    color="success"
                                    className="w-100"
                                    onClick={() => setBarcodeScannerOpen(true)}
                                >
                                    <i className="bx bx-barcode-reader me-1"></i>
                                    Escanear Código de Barras
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Ejemplo 4: Escaneo Múltiple */}
                    <Col lg={6}>
                        <Card className="h-100">
                            <CardHeader className="bg-soft-warning d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">
                                    <i className="bx bx-list-check me-2"></i>
                                    Ejemplo 4: Escaneo Múltiple
                                </h5>
                                {scannedItems.length > 0 && (
                                    <Badge color="warning" pill>
                                        {scannedItems.length} items
                                    </Badge>
                                )}
                            </CardHeader>
                            <CardBody>
                                <p className="text-muted mb-3">
                                    Escanea múltiples códigos para crear una lista.
                                    Evita duplicados automáticamente.
                                </p>

                                <div className="mb-3">
                                    <Button
                                        color="warning"
                                        className="w-100 mb-2"
                                        onClick={() => setMultiScannerOpen(true)}
                                    >
                                        <i className="bx bx-plus-circle me-1"></i>
                                        Agregar Código
                                    </Button>

                                    {scannedItems.length > 0 && (
                                        <Button
                                            color="light"
                                            className="w-100"
                                            onClick={clearScannedItems}
                                        >
                                            <i className="bx bx-trash me-1"></i>
                                            Limpiar Lista
                                        </Button>
                                    )}
                                </div>

                                {scannedItems.length > 0 && (
                                    <div className="scanned-items-list">
                                        {scannedItems.map((item, index) => (
                                            <div key={item.id} className="scanned-item">
                                                <span className="item-number">{index + 1}</span>
                                                <span className="item-code">{item.code}</span>
                                                <span className="item-time">{item.timestamp}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Información Adicional */}
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader>
                                <h5 className="card-title mb-0">
                                    <i className="bx bx-info-circle me-2"></i>
                                    Información del Componente
                                </h5>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md={6}>
                                        <h6>Formatos Soportados:</h6>
                                        <ul className="list-unstyled text-muted">
                                            <li><i className="bx bx-check text-success me-2"></i>QR Code</li>
                                            <li><i className="bx bx-check text-success me-2"></i>CODE-128</li>
                                            <li><i className="bx bx-check text-success me-2"></i>CODE-39 / CODE-93</li>
                                            <li><i className="bx bx-check text-success me-2"></i>EAN-13 / EAN-8</li>
                                            <li><i className="bx bx-check text-success me-2"></i>UPC-A / UPC-E</li>
                                            <li><i className="bx bx-check text-success me-2"></i>Data Matrix / PDF-417</li>
                                        </ul>
                                    </Col>
                                    <Col md={6}>
                                        <h6>Características:</h6>
                                        <ul className="list-unstyled text-muted">
                                            <li><i className="bx bx-check text-success me-2"></i>Cierre automático al detectar</li>
                                            <li><i className="bx bx-check text-success me-2"></i>Soporte cámara frontal/trasera</li>
                                            <li><i className="bx bx-check text-success me-2"></i>Manejo de errores integrado</li>
                                            <li><i className="bx bx-check text-success me-2"></i>Diseño responsive</li>
                                            <li><i className="bx bx-check text-success me-2"></i>Animaciones CSS</li>
                                            <li><i className="bx bx-check text-success me-2"></i>Hook personalizable</li>
                                        </ul>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Modales de Escaneo */}
                <BarcodeScannerModal
                    isOpen={productScannerOpen}
                    onClose={() => setProductScannerOpen(false)}
                    onScan={handleProductScan}
                    title="Escanear Producto"
                    scanType="both"
                />

                <BarcodeScannerModal
                    isOpen={qrScannerOpen}
                    onClose={() => setQrScannerOpen(false)}
                    onScan={handleQrScan}
                    title="Escanear Código QR"
                    scanType="qr"
                    facingMode="user"
                />

                <BarcodeScannerModal
                    isOpen={barcodeScannerOpen}
                    onClose={() => setBarcodeScannerOpen(false)}
                    onScan={handleBarcodeScan}
                    title="Escanear Código de Barras"
                    scanType="barcode"
                />

                <BarcodeScannerModal
                    isOpen={multiScannerOpen}
                    onClose={() => setMultiScannerOpen(false)}
                    onScan={handleMultiScan}
                    title="Agregar a la Lista"
                    scanType="both"
                />

            </Container>
        </div>
    );
};

export default BarcodeScannerDemo;